// sameRows
// --------
// Cheap structural-equality check for the "compare-before-setState" discipline the
// list loaders use (live-lists overhaul, Batch A1). A poll re-fetches a list every
// tick; without this, each loader calls setState with a brand-new array identity, so
// React reconciles the whole table (re-measure / re-sort / pagination + scroll churn)
// even when nothing actually changed.
//
// Usage — always via the functional-updater form so React can bail out of the render
// when the reference is unchanged:
//
//   setTasks((prev) => (sameRows(prev, data) ? prev : data));
//
// Returning the SAME `prev` reference tells React there is no update, so no re-render
// happens on an unchanged poll. When a row genuinely changed, the signatures differ
// and the new array flows through.
//
// The signature is a full JSON stringify, guarded by an identity + length short-circuit.
// The rows come from a fixed-column SELECT in a stable order, so key order is
// deterministic and two identical fetches serialize identically. List sizes here are
// tens-to-low-hundreds of rows, so this is far cheaper than the fetch that produced them.
// Order matters: two arrays with the same rows in a different order are NOT "same" (the
// list sort order is meaningful), which is the correct behaviour.

export function sameRows<T>(
  a: readonly T[] | null | undefined,
  b: readonly T[] | null | undefined
): boolean {
  if (a === b) return true;
  if (!Array.isArray(a) || !Array.isArray(b)) return false;
  if (a.length !== b.length) return false;
  return JSON.stringify(a) === JSON.stringify(b);
}
